import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ENTITY_CANDIDATE, ENTITY_RELATION, ENTITY_RELATION_CONTACT } from "../../utils/Constants";
import { isEntityOfType } from "../../utils/Helpers";
import Ows from "../../utils/OwsHandler";

interface VacancyUidsParams {
  uid: string;
  entity: string;
}

interface VacancyUidsState {
  vacancyUids: any[];
  loading: boolean;
  error: string | null;
}

const initialState: VacancyUidsState = {
  vacancyUids: [],
  loading: false,
  error: null,
};

const createRequestParams = (condition: any) => ({
  limit: 15,
  offset: 0,
  getTotalCount: true,
  sort: {
    entryDateTime: "DESC",
  },
  excludeLimitCheck: true,
  condition,
});

export const fetchVacancyUids = createAsyncThunk(
  "vacancy/fetchVacancyUids",
  async ({uid, entity}: VacancyUidsParams, { rejectWithValue }) => {
    try {
      let data;

      const condition: any = {
        type: "AND",
        items: [
          {
            type: "COND",
            field: "",
            op: "EQ",
            param: uid,
          },
        ],
      };

      switch (entity) {
        case ENTITY_CANDIDATE:
          condition.items[0].field = "candidateUid";
          break;
        case ENTITY_RELATION:
          condition.items[0].field = "relationId";
          break;
        case ENTITY_RELATION_CONTACT:
          condition.items[0].field = "relationContactId";
          break;
        default:
          throw new Error("Unknown entity type.");
      }

      if (!isEntityOfType(entity, ENTITY_CANDIDATE)) {
        condition.items.push({
          type: "COND",
          field: "passive",
          op: "EQ",
          param: false,
        });
      }

      if (isEntityOfType(entity, ENTITY_CANDIDATE)) {
        data = await Ows.handleRequest("Otys.Services.ProcedureService.getList", [
          {
            what: {
              vacancyUid: true,
            },
            ...createRequestParams(condition),
          },
        ]);
      } else {
        data = await Ows.handleRequest("Otys.Services.VacancyService.getList", [
          {
            what: {
              uid: true,
            },
            ...createRequestParams(condition),
          },
        ]);
      }
      
      if (data.response) {
        const uids = data.response.map((item: any) =>
          isEntityOfType(entity, ENTITY_CANDIDATE) ? item.vacancyUid : item.uid
        );
        return uids;
      } else {
        return rejectWithValue("No vacancies found.");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch vacancies.");
    }
  }
);

const vacancyUidsSlice = createSlice({
  name: "vacancyUids",
  initialState,
  reducers: {
    resetVacancyUids: (state) => {
      state.vacancyUids = [];
      state.loading = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVacancyUids.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVacancyUids.fulfilled, (state, action) => {
        state.vacancyUids = action.payload;
        state.loading = false;
      })
      .addCase(fetchVacancyUids.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export const { resetVacancyUids, clearError } = vacancyUidsSlice.actions;
export default vacancyUidsSlice.reducer;
